/** @fileoverview Renders a timeline block that lists saved entries for a record, oldest first. */

import { jsonPointerSegment } from '../../lib/previewSelection';
import { formatDateTime, textOf } from './helpers';
import StateBlock from './StateBlock';
import type { StateRendererProps } from './types';
import { useComponentStateRows } from './useComponentStateRows';

/**
 * Shows the title and one entry per saved row with when, who, and what.
 * @param props the timeline component and the record fields of the screen
 */
export default function TimelineRenderer({
  component,
  screenId,
  fields,
  inputs,
  selectionPath,
}: StateRendererProps<'timeline'>) {
  const { id: componentId, title } = component;
  const { isAvailable, isLoading, rows, error } = useComponentStateRows(
    componentId,
    fields,
    component.recordKey,
    screenId,
    inputs
  );

  return (
    <StateBlock
      title={title}
      selectionPath={selectionPath}
      isAvailable={isAvailable}
      isLoading={isLoading}
      hasRows={rows.length > 0}
      error={error}
      emptyText="Nothing logged yet."
    >
      <ol className="space-y-3 rounded-md border border-border bg-card px-3 py-3">
        {rows.map((row) => {
          const { key, value } = row;
          const rowPath = selectionPath
            ? `${selectionPath}/rows/${jsonPointerSegment(key)}`
            : undefined;
          const meta = [formatDateTime(value.at), textOf(value.by)].filter(Boolean).join(' · ');
          return (
            <li
              data-fabrik-selection-path={rowPath}
              className="border-l-2 border-border pl-3"
              key={key}
            >
              {meta && (
                <p
                  data-fabrik-selection-path={rowPath ? `${rowPath}/meta` : undefined}
                  className="type-caption text-muted-foreground"
                >
                  {meta}
                </p>
              )}
              <p
                data-fabrik-selection-path={rowPath ? `${rowPath}/text` : undefined}
                className="type-body whitespace-pre-wrap text-card-foreground"
              >
                {textOf(value.text)}
              </p>
            </li>
          );
        })}
      </ol>
    </StateBlock>
  );
}
